import { Notification, SummaryRow, SummaryView } from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateStepValidityAction } from '../../../actions';
import { useNewUserDispatch, useNewUserState } from '../../../context/useNewUserContext';
import { getSteps, IUser } from '../../../data/NewUserConstants';
import { buildNewUserPath } from '../../../utils/helpers';

export interface IProps {
  push: (href: string) => void;
  currentStep: number;
  isError?: boolean;
  errorMessage?: string;
}

/* eslint-disable react-hooks/exhaustive-deps */
export const SummaryStep: React.FC<IProps> = ({ currentStep, push, isError, errorMessage }: IProps) => {
  const { t } = useTranslation();

  const [notifcationOpen, setNotificationOpen] = useState(isError);
  const { validSteps, user } = useNewUserState();
  const dispatch = useNewUserDispatch();

  const steps = getSteps(t);

  useEffect(() => {
    updateStepValidityAction(true, validSteps, currentStep, dispatch);

    return () => toggleNotification(false);
  }, []);

  useEffect(() => {
    setNotificationOpen(isError);
  }, [isError]);

  const handleUserEditClick = () => {
    push(buildNewUserPath(steps[0].route));
  };

  const handleAccessLevelEditClick = () => {
    push(buildNewUserPath(steps[1].route));
  };

  const handleRoleEditClick = () => {
    push(buildNewUserPath(steps[2].route));
  };

  const toggleNotification = (isOpen: boolean) => {
    setNotificationOpen(isOpen);
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return (
    <>
      <Notification isOpen={notifcationOpen} variation="error" onChange={toggleNotification}>
        {errorMessage}
      </Notification>

      <SummaryView heading={t('newUser.summaryStep.heading')} subHeading={t('newUser.summaryStep.subHeading')}>
        <SummaryRow
          heading={t('newUser.summaryStep.emailRow.heading')}
          description={getEmailText(user, t)}
          buttonText={t('newUser.summaryStep.emailRow.button')}
          onClick={handleUserEditClick}
          data-testid="summary-email"
        />

        <SummaryRow
          heading={t('newUser.summaryStep.newUserRow.heading')}
          description={getUserText(user, t)}
          buttonText={t('newUser.summaryStep.newUserRow.button')}
          onClick={handleUserEditClick}
          data-testid="summary-user"
        />

        <SummaryRow
          heading={t('newUser.summaryStep.accessLevelRow.heading')}
          description={getAccessLevelText(user, t)}
          buttonText={t('newUser.summaryStep.accessLevelRow.button')}
          onClick={handleAccessLevelEditClick}
          data-testid="summary-accessLevel"
        />

        <SummaryRow
          heading={t('newUser.summaryStep.roleRow.heading')}
          description={t('newUser.summaryStep.roleRow.description', {
            name: user.name,
            role: user.selectedRole.label,
            permissions: user.rolePermissions.join(', '),
          })}
          buttonText={t('newUser.summaryStep.roleRow.button')}
          onClick={handleRoleEditClick}
          data-testid="summary-role"
        />
      </SummaryView>
    </>
  );
};

const getUserText = (user: IUser, t: TFunction): string => {
  return t('newUser.summaryStep.newUserRow.description', {
    name: user.name,
  });
};

const getEmailText = (user: IUser, t: TFunction): string => {
  return t('newUser.summaryStep.emailRow.description', {
    email: user.email,
  });
};

const getAccessLevelText = (user: IUser, t: TFunction) => {
  switch (user.selectedAccessLevel.value) {
    case 'SaleCycle':
      return t('newUser.summaryStep.accessLevelRow.SaleCycleDescription', {
        name: user.name,
      });
    case 'Brand':
      return t('newUser.summaryStep.accessLevelRow.BrandDescription', {
        name: user.name,
        brand: user?.selectedBrand?.label ?? '???',
      });
    case 'Site':
      return t('newUser.summaryStep.accessLevelRow.SiteDescription', {
        name: user.name,
        site: user?.selectedSite?.label ?? '???',
      });

    case 'MultiSite':
      return t('newUser.summaryStep.accessLevelRow.MultiSiteDescription', {
        name: user.name,
        group: user.selectedMultiSiteGroup.label,
        sites: user.selectedMultiSites.map(v => v.label).join(', '),
      });
  }
};

export default SummaryStep;
